import { Checkbox, Form, Modal, Stack, TextField } from "@shopify/polaris";
import React, { useCallback, useReducer } from "react";
import { get } from "lodash";

import { handleError, reducerFn } from "../../../helper";
import { useMutation } from "@apollo/react-hooks";
import { DUPLICATE_MAPPING_ORDER } from "../../../graphql/mutations";
import { useToastContext } from "../../shared/ToastContext";
import useTimeout from "../../../hooks/useTimeout";

export default function TiktokDuplicateOrder({
    item,
    open,
    onClose,
    onCompleted,
}) {
    const [state, setState] = useReducer(reducerFn, {
        orders: "1",
        quantityPerItem: "1",
        setQuantity: false,
        items: [],
    });

    const lineId = getLineItemId(item);

    const { setNotify, toggleToast } = useToastContext();

    const [setDelay] = useTimeout();
    const [duplicateMappingOrder, { loading }] = useMutation(
        DUPLICATE_MAPPING_ORDER,
        {
            onCompleted: () => {
                setNotify({
                    msg: "Mapping Order duplicate success",
                    err: false,
                });

                setDelay(() => {
                    onCompleted && onCompleted();
                })
            },
            onError: (err) => {
                setNotify({ msg: handleError(err?.toString()), err: true });
            },
        },
    );

    const handleSubmit = useCallback(() => {
        if (!item || !item.id) return;

        toggleToast && toggleToast(true);
        setNotify && setNotify({ msg: null, err: false });

        duplicateMappingOrder({
            variables: {
                input: {
                    originMappingOrderID: item.id,
                    totalItems: Number(state.orders),
                    quantityPerItem: Number(state.quantityPerItem),
                    items: !!state.setQuantity ? state.items : undefined,
                },
            },
        });
    }, [duplicateMappingOrder, setNotify, toggleToast, state, item?.id]);

    const handleStateChange = useCallback(
        (value, id) => {
            const newState = { [id]: value };

            if (["orders", "quantityPerItem"].includes(id)) {
                let total = state.orders;
                if (id === "orders") {
                    total = value;
                }

                let qty = state.quantityPerItem;
                if (id === "quantityPerItem") {
                    qty = value;
                }

                newState.items = genItems(lineId, total, qty);
            }

            setState(newState);
        },
        [state, lineId],
    );

    const handleLineChange = useCallback(
        (index) => (value, id) => {
            const item = state.items[index];

            if (!item) return;

            item[id] = value;

            const newItems = [...state.items];

            newItems[index] = item;

            setState((p) => ({ ...p, items: newItems }));
        },
        [state.items],
    );

    return (
        <Modal
            title="Tiktok Order Duplicate"
            sectioned
            open={open}
            onClose={onClose}
            primaryAction={{
                content: "Submit",
                onAction: handleSubmit,
                loading: loading,
            }}
            secondaryActions={[{ content: "Cancel", onAction: onClose }]}
        >
            <Stack vertical>
                <TextField
                    id="orders"
                    label="Add more orders"
                    type="number"
                    min={1}
                    value={state.orders}
                    onChange={handleStateChange}
                    placeholder="Add more orders"
                />

                <TextField
                    id="quantityPerItem"
                    label="Quantity per Order"
                    type="number"
                    min={1}
                    value={state.quantityPerItem}
                    onChange={handleStateChange}
                    placeholder="Enter quantity"
                />

                <Checkbox
                    label="Set the quantity for each order"
                    id="setQuantity"
                    checked={state.setQuantity}
                    onChange={handleStateChange}
                />

                {state.setQuantity && state.items?.length > 0 ? (
                    <div style={{ paddingLeft: "1rem" }}>
                        <Stack vertical>
                            {state.items.map((item, index) => (
                                <Stack
                                    key={`item-${item.lineItemID}`}
                                    distribution="fill"
                                >
                                    <TextField
                                        id="lineItemID"
                                        label={`Line item ID #${index + 1}`}
                                        value={item.lineItemID}
                                        onChange={handleLineChange(index)}
                                        placeholder="Enter Line item ID"
                                    />

                                    <TextField
                                        id="quantity"
                                        label="Quantity"
                                        type="number"
                                        min={1}
                                        value={item.quantity}
                                        onChange={handleLineChange(index)}
                                        placeholder="Enter quantity"
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    </div>
                ) : null}
            </Stack>
        </Modal>
    );
}

function getLineItemId(data) {
    return get(data, "orderData.line_id");
}

function genItems(lineId, total, qty) {
    const res = [];

    let lastLineId = lineId;
    for (let i = 0; i < total; i++) {
        lastLineId = genLineItemId(lastLineId);

        res.push({ lineItemID: lastLineId, quantity: qty });
    }

    return res;
}

function genLineItemId(oldLineId) {
    const splitted = (oldLineId || "").split(/\-/);

    let index = 1;

    if (splitted.length > 1) {
        if (isNaN(splitted[1])) {
            index++;
        } else {
            index += Number(splitted[1]);
        }
    }

    return `${splitted[0]}-${index}`;
}
